import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

/**
 * Collects the section ids of every cart-items-component currently on the
 * page (cart drawer and/or full cart page), so add/remove requests can ask
 * the Section Rendering API to return fresh HTML for all of them.
 * @returns {string[]}
 */
function getCartSectionIds() {
  const ids = [];
  document.querySelectorAll('cart-items-component').forEach((item) => {
    if (item instanceof HTMLElement && item.dataset.sectionId) {
      ids.push(item.dataset.sectionId);
    }
  });
  return ids;
}

/**
 * Fetches the full cart object (used after /cart/add.js, whose response
 * only contains the added line items rather than cart totals).
 * @returns {Promise<any>}
 */
function fetchCart() {
  return fetch(`${Theme.routes.cart_url}.json`, {
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
  }).then((response) => {
    if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status}`);
    return response.json();
  });
}

/**
 * A small "+ Add" button used in the cart's "Frequently bought together"
 * list. Adds a single variant to the cart via the standard Ajax Cart API and
 * announces the change through the theme's CartLinesUpdateEvent so the cart
 * drawer/page section morphs itself with the fresh state.
 */
class CartFbtAddButton extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.#onClick);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    event.preventDefault();

    if (this.dataset.pending === 'true' || this.classList.contains('is-added')) return;

    const variantId = this.dataset.variantId;
    if (!variantId) return;

    this.dataset.pending = 'true';
    this.classList.add('is-loading');

    const sectionIds = getCartSectionIds();
    const deferred = CartLinesUpdateEvent.createPromise();

    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: [{ merchandiseId: variantId, quantity: 1 }],
        promise: deferred.promise,
      })
    );

    fetch(Theme.routes.cart_add_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        items: [{ id: Number(variantId), quantity: 1 }],
        sections: sectionIds.join(','),
      }),
    })
      .then((response) => response.json())
      .then(async (response) => {
        if (response.status) {
          const message = response.message || 'Could not add item to cart';
          this.dispatchEvent(new CartErrorEvent({ error: message, code: 'INVALID' }));
          deferred.reject(new Error(message));
          return;
        }

        const cart = await fetchCart();

        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: {
            items: cart.items,
            sections: response.sections,
            source: 'cart-fbt-add-button',
            didError: false,
          },
        });

        this.classList.add('is-added');
        this.classList.remove('is-loading');
      })
      .catch((error) => {
        console.error('[cart-fbt-add-button]', error);
        deferred.reject(error);
        this.dispatchEvent(
          new CartErrorEvent({ error: error?.message || 'Network error adding item', code: 'SERVICE_UNAVAILABLE' })
        );
      })
      .finally(() => {
        this.dataset.pending = 'false';
        this.classList.remove('is-loading');
      });
  };
}

if (!customElements.get('cart-fbt-add-button')) {
  customElements.define('cart-fbt-add-button', CartFbtAddButton);
}

/**
 * The "Shipping Protection" opt-in toggle. Adds or removes a single fixed-
 * price service variant from the cart via the standard Ajax Cart API,
 * announcing the change the same way the rest of the theme's cart
 * interactions do so the drawer/page section refreshes itself.
 */
class CartShippingProtectionToggle extends HTMLElement {
  /** @type {HTMLInputElement | null} */
  #checkbox = null;

  connectedCallback() {
    this.#checkbox = this.querySelector('input[type="checkbox"]');
    this.#checkbox?.addEventListener('change', this.#onChange);
  }

  disconnectedCallback() {
    this.#checkbox?.removeEventListener('change', this.#onChange);
  }

  /** @param {Event} event */
  #onChange = (event) => {
    const input = /** @type {HTMLInputElement} */ (event.target);
    const variantId = this.dataset.variantId;
    if (!variantId) return;

    const adding = input.checked;

    this.classList.add('is-loading');
    input.disabled = true;

    const sectionIds = getCartSectionIds();
    const deferred = CartLinesUpdateEvent.createPromise();

    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: adding ? 'add' : 'remove',
        context: 'cart',
        lines: [{ merchandiseId: variantId, quantity: adding ? 1 : 0 }],
        promise: deferred.promise,
      })
    );

    const request = adding
      ? fetch(Theme.routes.cart_add_url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            items: [{ id: Number(variantId), quantity: 1 }],
            sections: sectionIds.join(','),
          }),
        }).then((response) => response.json())
      : fetch(Theme.routes.cart_change_url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            // /cart/change expects `id` as a string matching the line item
            // key format (digits, or "digits:propertieshash") — a JSON
            // number is rejected with a 400.
            id: String(variantId),
            quantity: 0,
            sections: sectionIds.join(','),
          }),
        }).then((response) => response.json());

    request
      .then(async (response) => {
        if (response.status || response.errors) {
          const message = response.message || response.errors || 'Could not update shipping protection';
          this.dispatchEvent(new CartErrorEvent({ error: message, code: 'INVALID' }));
          deferred.reject(new Error(message));
          input.checked = !adding;
          return;
        }

        // /cart/add.js only returns the added line items, so fetch the full
        // cart for accurate totals. /cart/change.js already returns it.
        const cart = adding ? await fetchCart() : response;

        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: {
            items: cart.items,
            sections: response.sections,
            source: 'cart-shipping-protection-toggle',
            didError: false,
          },
        });
      })
      .catch((error) => {
        console.error('[cart-shipping-protection-toggle]', error);
        deferred.reject(error);
        input.checked = !adding;
        this.dispatchEvent(
          new CartErrorEvent({ error: error?.message || 'Network error updating cart', code: 'SERVICE_UNAVAILABLE' })
        );
      })
      .finally(() => {
        this.classList.remove('is-loading');
        if (this.#checkbox) this.#checkbox.disabled = false;
      });
  };
}

if (!customElements.get('cart-shipping-protection-toggle')) {
  customElements.define('cart-shipping-protection-toggle', CartShippingProtectionToggle);
}
